import React, { useEffect, useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { NotePencil, FloppyDisk, MapTrifold, X, ArrowSquareOut } from "@phosphor-icons/react";
import { api, money } from "@/lib/api";
import { toast } from "sonner";

export function SatelliteView({ address, height = 150 }) {
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  if (!address || !address.trim() || failed) return null;
  const mapsLink = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  return (
    <>
      <div className="relative mt-3 rounded-md overflow-hidden border border-slate-200 bg-slate-100 group cursor-pointer" style={{ height }} onClick={() => setOpen(true)} data-testid="satellite-thumb">
        {!loaded && <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-xs"><MapTrifold size={20} className="mr-1.5" /> Loading satellite…</div>}
        <img
          src={api.satelliteUrl(address, { width: 500, height: 300 })}
          alt={`Satellite view of ${address}`}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
          loading="lazy"
        />
        <div className="absolute top-1.5 right-1.5 bg-black/55 text-white text-[10px] font-semibold px-2 py-0.5 rounded flex items-center gap-1"><MapTrifold size={11} /> Satellite</div>
      </div>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-6" onClick={() => setOpen(false)} data-testid="satellite-lightbox">
          <button onClick={() => setOpen(false)} className="absolute top-5 right-5 text-white/80 hover:text-white"><X size={30} /></button>
          <div onClick={(e) => e.stopPropagation()} className="max-w-3xl w-full">
            <img src={api.satelliteUrl(address, { width: 640, height: 480, zoom: 20 })} alt={address} className="w-full rounded-lg" />
            <div className="mt-3 flex items-center justify-between text-white">
              <span className="text-sm">{address}</span>
              <a href={mapsLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[#00B4D8] hover:underline text-sm" onClick={(e) => e.stopPropagation()}>Open in Google Maps <ArrowSquareOut size={15} /></a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function NotesCard({ value, onSave, title = "Notes" }) {
  const [text, setText] = useState(value || "");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setText(value || ""); setDirty(false); }, [value]);

  const save = async () => {
    setSaving(true);
    try { await onSave(text); setDirty(false); toast.success("Notes saved"); }
    catch (e) { toast.error("Failed to save notes"); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-slate-700"><NotePencil size={18} weight="duotone" className="text-[#00B4D8]" /><h3 className="font-display font-semibold">{title}</h3></div>
        {dirty && <Button size="sm" onClick={save} disabled={saving} className="bg-[#0B1726] hover:bg-slate-800" data-testid="save-notes-button"><FloppyDisk size={15} className="mr-1" /> {saving ? "Saving…" : "Save"}</Button>}
      </div>
      <Textarea value={text} onChange={(e) => { setText(e.target.value); setDirty(true); }} placeholder="Add notes, access instructions, gate codes, special requests…" className="min-h-[120px] resize-y" data-testid="notes-textarea" />
    </div>
  );
}

export function LineItemsView({ items = [], subtotal, tax, taxRate, total, doc = {} }) {
  const discountAmount = Number(doc.discount_amount || 0);
  const discountLabel = doc.discount_type === "percent" ? `Discount (${doc.discount_value}%)` : "Discount";
  const depositAmount = Number(doc.deposit_amount || 0);
  const depositPaid = Number(doc.deposit_paid || 0);
  const amountDue = doc.amount_due != null ? Number(doc.amount_due) : null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
      <div className="grid grid-cols-12 gap-2 bg-slate-50 px-5 py-2.5 text-[11px] font-bold uppercase tracking-wider text-slate-500">
        <div className="col-span-6">Description</div><div className="col-span-2 text-center">Qty</div>
        <div className="col-span-2 text-right">Rate</div><div className="col-span-2 text-right">Amount</div>
      </div>
      {items.map((li, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 px-5 py-3 border-t border-slate-100 text-sm">
          <div className="col-span-6 text-slate-800">{li.description}</div>
          <div className="col-span-2 text-center text-slate-500">{li.quantity}</div>
          <div className="col-span-2 text-right text-slate-500">{money(li.unit_price)}</div>
          <div className="col-span-2 text-right font-semibold">{money(li.quantity * li.unit_price)}</div>
        </div>
      ))}
      <div className="border-t border-slate-200 px-5 py-4 space-y-1.5 bg-slate-50/50">
        <div className="flex justify-between text-sm"><span className="text-slate-500">Subtotal</span><span className="font-medium">{money(subtotal)}</span></div>
        {Number(tax) > 0 && <div className="flex justify-between text-sm"><span className="text-slate-500">Tax ({taxRate}%)</span><span className="font-medium">{money(tax)}</span></div>}
        {discountAmount > 0 && <div className="flex justify-between text-sm"><span className="text-slate-500">{discountLabel}</span><span className="font-medium text-rose-600">-{money(discountAmount)}</span></div>}
        <div className="flex justify-between pt-2 border-t border-slate-200"><span className="font-bold text-slate-900">Total</span><span className="font-display font-extrabold text-lg text-[#00B4D8]">{money(total)}</span></div>
        {depositPaid > 0 ? (
          <>
            <div className="flex justify-between text-sm pt-1"><span className="text-slate-500">Deposit Paid</span><span className="font-medium text-emerald-600">-{money(depositPaid)}</span></div>
            {amountDue != null && <div className="flex justify-between text-sm"><span className="font-semibold text-slate-700">Balance Due</span><span className="font-bold text-[#00B4D8]">{money(amountDue)}</span></div>}
          </>
        ) : depositAmount > 0 ? (
          <div className="flex justify-between text-sm pt-1"><span className="text-slate-500">Deposit Due ({doc.deposit_pct}%)</span><span className="font-semibold text-[#00B4D8]">{money(depositAmount)}</span></div>
        ) : null}
      </div>
    </div>
  );
}
